/**
 * Client-side filter: hides menu collection links with no available products.
 * Runs after page render so filtering is done before user hovers navigation.
 */
(function () {
  'use strict';

  const CACHE_KEY = 'menu_empty_collections';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const CONCURRENCY = 8;
  const REQUEST_TIMEOUT_MS = 1800;
  let activeRun = null;

  function isDebugMode() {
    return document.querySelector('[data-empty-debug="true"]') !== null;
  }

  function getCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > CACHE_TTL_MS) return {};
      return parsed.data || {};
    } catch {
      return {};
    }
  }

  function setCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: data, ts: Date.now() }));
    } catch (_) {}
  }

  function checkCollectionHasAvailable(handle) {
    const url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=1&filter.v.availability=1';
    if (typeof AbortController === 'undefined') {
      return fetch(url)
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (json) {
          if (!json || !json.products) return false;
          return json.products.length > 0;
        })
        .catch(function () { return true; });
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    return fetch(url, { signal: controller.signal })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        if (!json || !json.products) return false;
        return json.products.length > 0;
      })
      .finally(function () {
        window.clearTimeout(timeoutId);
      })
      .catch(function () { return true; });
  }

  function hasChildMenuItems(el) {
    return el.querySelector('ul li') !== null;
  }

  function applyEmpty(el) {
    if (hasChildMenuItems(el)) return;
    if (isDebugMode()) {
      if (el.querySelector('[data-empty-marker]')) return;
      var link = el.tagName === 'A' ? el : el.querySelector('a');
      if (!link) link = el;
      var span = document.createElement('span');
      span.setAttribute('data-empty-marker', '');
      span.textContent = ' (empty)';
      span.style.color = 'red';
      link.appendChild(span);
    } else {
      el.style.display = 'none';
    }
  }

  /** Shop-by column: still visible if untracked link or a collection row that is not empty-filtered. */
  function isStillVisibleAfterFilter(li) {
    if (!li.getAttribute('data-collection-handle')) return true;
    if (isDebugMode()) return li.querySelector('[data-empty-marker]') === null;
    return li.style.display !== 'none';
  }

  /**
   * Hide .shopby__col when every list row is empty (or only empty collection links),
   * unless the column's own collection (data-collection-handle on .shopby__col) still has available products.
   */
  function hideEmptyShopByColumns(containers, cache) {
    cache = cache || {};
    containers.forEach(function (container) {
      container.querySelectorAll('.shopby__col').forEach(function (col) {
        var rows = col.querySelectorAll('.shopby__col-list > li');
        if (rows.length === 0) return;

        var anyVisible = false;
        for (var i = 0; i < rows.length; i++) {
          if (isStillVisibleAfterFilter(rows[i])) {
            anyVisible = true;
            break;
          }
        }
        if (anyVisible) return;

        var parentHandle = col.getAttribute('data-collection-handle');
        if (parentHandle && cache[parentHandle] === true) {
          return;
        }

        if (isDebugMode()) {
          var title = col.querySelector('.shopby__col-title');
          if (title && !title.querySelector('[data-empty-column-marker]')) {
            var mark = document.createElement('span');
            mark.setAttribute('data-empty-column-marker', '');
            mark.textContent = ' (column empty)';
            mark.style.color = 'red';
            title.appendChild(mark);
          }
        } else {
          col.style.display = 'none';
        }
      });
    });
  }

  function hideEmptyShopByTabs(containers) {
    if (isDebugMode()) return;

    containers.forEach(function (container) {
      if (!(container instanceof HTMLElement)) return;

      var tabs = Array.prototype.slice.call(container.querySelectorAll('[data-shopby-tab]'));
      var panels = Array.prototype.slice.call(container.querySelectorAll('[data-shopby-panel]'));
      if (tabs.length === 0 || panels.length === 0) return;

      var panelByKey = {};
      panels.forEach(function (panel) {
        var key = panel.getAttribute('data-shopby-panel');
        if (!key) return;
        panelByKey[key] = panel;
      });

      var visibleKeys = [];
      tabs.forEach(function (tab) {
        var key = tab.getAttribute('data-shopby-tab');
        if (!key) return;
        var panel = panelByKey[key];
        if (!panel) return;

        var cols = panel.querySelectorAll('.shopby__col');
        var hasVisibleCols = false;
        for (var i = 0; i < cols.length; i++) {
          if (cols[i].style.display !== 'none') {
            hasVisibleCols = true;
            break;
          }
        }

        var tabItem = tab.closest('.shopby__tab-item');
        if (!hasVisibleCols) {
          if (tabItem instanceof HTMLElement) {
            tabItem.style.display = 'none';
          } else {
            tab.style.display = 'none';
          }
          panel.setAttribute('hidden', '');
          return;
        }

        if (tabItem instanceof HTMLElement) {
          tabItem.style.display = '';
        } else {
          tab.style.display = '';
        }
        visibleKeys.push(key);
      });

      if (visibleKeys.length === 0) return;

      var activeKey = visibleKeys[0];
      tabs.forEach(function (tab) {
        var key = tab.getAttribute('data-shopby-tab');
        if (!key) return;
        var isActive = key === activeKey;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      panels.forEach(function (panel) {
        var key = panel.getAttribute('data-shopby-panel');
        if (!key) return;
        var isActive = key === activeKey;
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    });
  }

  function runFilter() {
    const containers = document.querySelectorAll('[data-hide-empty-collections="true"]');
    if (containers.length === 0) return Promise.resolve();

    const cache = getCache();
    const toFetch = [];
    const elementsByHandle = {};

    containers.forEach(function (container) {
      container.querySelectorAll('[data-collection-handle]').forEach(function (el) {
        const handle = el.getAttribute('data-collection-handle');
        if (!handle) return;
        if (cache[handle] !== undefined) {
          if (!cache[handle]) applyEmpty(el);
          return;
        }
        if (!elementsByHandle[handle]) {
          elementsByHandle[handle] = [];
          toFetch.push(handle);
        }
        elementsByHandle[handle].push(el);
      });
    });

    if (toFetch.length === 0) {
      hideEmptyShopByColumns(containers, cache);
      hideEmptyShopByTabs(containers);
      return Promise.resolve();
    }

    var index = 0;
    function next() {
      if (index >= toFetch.length) return Promise.resolve();
      var handle = toFetch[index++];
      return checkCollectionHasAvailable(handle).then(function (hasAvailable) {
        cache[handle] = hasAvailable;
        return next();
      });
    }

    var workers = [];
    for (var i = 0; i < Math.min(CONCURRENCY, toFetch.length); i++) {
      workers.push(next());
    }
    return Promise.all(workers).then(function () {
      Object.keys(cache).forEach(function (handle) {
        if (!cache[handle]) {
          (elementsByHandle[handle] || []).forEach(function (el) {
            applyEmpty(el);
          });
        }
      });
      setCache(cache);
      hideEmptyShopByColumns(containers, cache);
      hideEmptyShopByTabs(containers);
    });
  }

  function unlockMenu() {
    document.body.classList.remove('menu-filtering');
  }

  function init() {
    if (document.querySelectorAll('[data-hide-empty-collections="true"]').length === 0) return;
    if (activeRun) return;
    document.body.classList.add('menu-filtering');

    activeRun = runFilter();
    activeRun
      .catch(function () {})
      .finally(function () {
        unlockMenu();
        activeRun = null;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    window.addEventListener('load', init, { once: true });
  } else {
    init();
  }
  window.addEventListener('pagereveal', function (e) {
    if (e.viewTransition) init();
  });
  window.addEventListener('section-morphed', init);
})();
